import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Links to one record in the admin app.
 *
 * `/admin-app/students?student=<id>` narrows the Students list to that child with their row
 * already open; `?parent=` on Parents, `?invoice=` and `?payment=` on Finance Ops and
 * `?enrollment=` on Enrollments do the same for theirs. The command palette is the first caller, but anything
 * that names a record can link here — the links are plain URLs, so they also survive a
 * refresh and can be pasted to another admin.
 */
export const STUDENT_PARAM = 'student';
export const PARENT_PARAM = 'parent';
export const INVOICE_PARAM = 'invoice';
export const ENROLLMENT_PARAM = 'enrollment';
export const PAYMENT_PARAM = 'payment';

export const studentHref = (id: string) =>
  `/admin-app/students?${STUDENT_PARAM}=${encodeURIComponent(id)}`;

export const parentHref = (id: string) =>
  `/admin-app/parents?${PARENT_PARAM}=${encodeURIComponent(id)}`;

/** Invoices is the Finance Ops default tab, so the link needs no `tab`. */
export const invoiceHref = (id: string) =>
  `/admin-app/finances?${INVOICE_PARAM}=${encodeURIComponent(id)}`;

/** Payments is not the Finance Ops default tab, so the link names it. */
export const paymentHref = (id: string) =>
  `/admin-app/finances?tab=payments&${PAYMENT_PARAM}=${encodeURIComponent(id)}`;

/** One enrollment is a row of the Details list; the Summary pivot has no rows to open. */
export const enrollmentHref = (id: string) =>
  `/admin-app/enrollments?view=details&${ENROLLMENT_PARAM}=${encodeURIComponent(id)}`;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value: string) => UUID_RE.test(value);

/**
 * The record a page was linked to, and a way to let go of it.
 *
 * Anything that is not a uuid reads as no link at all. The id goes straight into a filter on
 * a uuid column, and Postgres rejects a malformed one with an error that would replace the
 * whole page — a stale or hand-edited link should just show the full list instead.
 *
 * Letting go uses `replace`, the same as the tab state: back from the full list should
 * return to wherever the admin was before following the link, not to the narrowed view.
 *
 * `clear` takes an optional edit for any other param that changes in the same click. Two
 * separate writes would not compose: each `setSearchParams` starts from the params of the
 * last render, so the second write would put the link back.
 */
export function useFocusedRecordId(
  param: string,
): [string | null, (edit?: (params: URLSearchParams) => void) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(param);
  const id = raw !== null && isUuid(raw) ? raw : null;

  // react-router rebuilds `setSearchParams` on every URL change; a latest-ref keeps `clear`
  // stable for callers' dependency arrays (see useUrlTabState).
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const clear = useCallback((edit?: (params: URLSearchParams) => void) => {
    setSearchParamsRef.current(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(param);
        edit?.(next);
        return next;
      },
      { replace: true },
    );
  }, [param]);

  return [id, clear];
}
